import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import appState from '../../state/AppStateContainer';
import { ServiceUser } from '../../services/service-user';

import SearchBarWithActions from '../../components/shared/SearchBarWithActions/SearchBarWithActions';
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import { matchesLabelOrValue } from '../bt-checklist-select/checklist';
import MaterialTable from 'material-table';
import './user-list.css';
import { CustomAvatar, Loading } from '../../components/shared';
import OverflowTip from '../../components/shared/OverflowTip/OverflowTip';
import OptionsMenu from '../../components/shared/OptionsMenu/OptionsMenu';
import { serviceProfile } from '../../services/service-profile';
import { ServiceProjects } from '../../services/service-projects';
import AddMember from './add-member';
import appEnum from '../../util/appEnum';
import Modal from '../../components/shared/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { isAfter } from 'date-fns';
import { NotificationManager } from 'react-notifications';
import { canManageOrgSettings } from '../../util/settingsAccess';

const UserList = ({ addMemberModalOpen, setAddMemberModalOpen }) => {
  const tableRef = useRef();
  const appAddMemberRef = useRef();
  const [allColleagues, setAllColleagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [screenLoader, setScreenLoader] = useState(false);
  const [currentMemberToEdit, setCurrentMemberToEdit] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [submitBtnLoader, setSubmitBtnLoader] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [roleFilter, setRoleFilter] = useState([]);
  const [t] = useTranslation('users');
  const [showRemoveUserConfirmationModal, setShowRemoveUserConfirmationModal] = useState(false);
  const [showRenewInvitationConfirmationModal, setShowRenewInvitationConfirmationModal] = useState(false);
  const [currentMemberToRemoveOrRenew, setCurrentMemberToRemoveOrRenew] = useState(null);
  const [confirmationModalTitle, setConfirmationModalTitle] = useState('');
  const canManage = canManageOrgSettings(appState.getUserRole());
  const canEditRows = canManage && !appState.superAdminOrgAndUser();
  const canEditMember = rowData => canEditRows && !!rowData?._id;

  const roleLabels = {
    [appEnum.Role.Client]: t('role.client', 'Beställare'),
    [appEnum.Role.Kalkyl]: t('role.visitor', 'Besökare'),
    [appEnum.Role.Inköp]: t('role.participant', 'Deltagare'),
    [appEnum.Role.Admin]: t('role.admin', 'Administratör'),
    [appEnum.Role.SuperAdmin]: t('role.superAdmin', 'Superadmin'),
  }

  const roleFilterOptions = useMemo(() => [
    { value: appEnum.Role.Admin, label: t('role.admin', 'Administratör') },
    { value: 'Inköp', label: t('role.participant', 'Deltagare') },
    { value: 'Kalkyl', label: t('role.visitor', 'Besökare') },
    { value: 'Client', label: t('role.client', 'Beställare') },
    { value: 'unanswered', label: t('role.unanswered', 'Obesvarad inbjudan') },
  ], [t]);

  useEffect(() => {
    const startProcessing = async () => {
      setLoading(true);
      try {
        let _colleagues = await serviceProfile.getColleagues();
        setAllColleagues(_colleagues);
        tableRef?.current?.dataManager?.changePageSize(_colleagues.length);
      } catch (e) {
        console.error('error', e);
      }

      setLoading(false);
      appState.setState({ loader: false });
    };

    startProcessing().catch(console.error);
  }, []);

  useEffect(() => () => setAddMemberModalOpen(false), [setAddMemberModalOpen]);

  useEffect(() => {
    if (!addMemberModalOpen) return;
    ServiceProjects.getProjectList()
      .then(setProjectList)
      .catch(console.error)
      .finally(() => setScreenLoader(false));
  }, [addMemberModalOpen]);

  const colleagues = useMemo(() => {
    let res = allColleagues
      .filter(user => {
        const search = searchValue.toLowerCase();
        const matchesSearch = !searchValue ||
          (user.name.toLowerCase().startsWith(search) ||
           (user.email && user.email.toLowerCase().includes(search)));

        const matchesRole = roleFilter.length === 0 ||
          roleFilter.some(role => {
            if (role === 'unanswered') {
              return user.invitationCode && !user.isActive;
            }
            return user.role === role;
          });

        return matchesSearch && matchesRole;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    tableRef?.current?.dataManager?.changePageSize(res.length);
    return res;
  }, [allColleagues, searchValue, roleFilter]);

  const removeInternLocally = async internId => {
    setShowRemoveUserConfirmationModal(false);
    try {
      await ServiceUser.removeUser(internId);
      NotificationManager.success(
        t('notification.removeUser.description.success', 'Användaren har tagits bort')
      );
      setAllColleagues(allColleagues.filter(i => i._id !== internId));
    } catch (e) {
      NotificationManager.error(
        e?.response?.data?.error,
        t('notification.removeUser.description.error', 'Användaren kunde inte tas bort')
      );
    }
  };

  const onRenewInvitation = async email => {
    setShowRenewInvitationConfirmationModal(false);
    const response = await serviceProfile.renewUserInvitation({ email });
    if (response.status === 200) {
      NotificationManager.success(
        t('notification.renewInvitation.description.success', 'Ny inbjudan skickad')
      );
      const _allColleagues = [...allColleagues];
      let selectedInternIdx = _allColleagues.findIndex(i => i.email === email);
      _allColleagues[selectedInternIdx] = response.user;
      setAllColleagues(_allColleagues);
    } else {
      NotificationManager.error(
        t('notification.renewInvitation.description.error', 'Det gick inte att skicka den förnyade inbjudan')
      );
    }
  }

  const onRemoveOrRenewConfirmation = () => {
    if (showRemoveUserConfirmationModal) {
      removeInternLocally(currentMemberToRemoveOrRenew?._id);
    } else if (showRenewInvitationConfirmationModal) {
      onRenewInvitation(currentMemberToRemoveOrRenew?.email);
    }
  };


  const getColumns = () => {
    return [
      {
        title: t('table.header.name', 'Namn'),
        cellStyle: { fontSize: null },
        field: 'name',
        render: rowData => {
          const unansweredInvitation = !rowData?.isActive && rowData.invitationCode;
          const expiredInvitation = unansweredInvitation && rowData.invitationCodeExpirationDate ? isAfter(new Date(), new Date(rowData.invitationCodeExpirationDate)) : false;
          const name = expiredInvitation ? t('invitation.expiredInvitation', 'Obesvarad inbjudan - har löpt ut') : unansweredInvitation ? t('invitation.unansweredInvitation', 'Obesvarad inbjudan') : rowData?.name;
          return(
          <div className="name">
            <CustomAvatar alt={unansweredInvitation ? '?' : rowData?.name} src={rowData?.avatarUrl} />
            <span>{name}</span>
          </div>
        )},
      },
      {
        title: t('table.header.eMail', 'E-post'),
        cellStyle: { fontSize: null },
        field: 'email',
        render: rowData => <div className="email">{rowData?.email}</div>,
      },
      {
        title: t('table.header.role', 'Roll'),
        cellStyle: { fontSize: null },
        field: 'role',
        render: rowData => <div className="role">{roleLabels[rowData?.role]}</div>,
      },
      {
        title: t('table.header.project', 'Projekt'),
        cellStyle: { fontSize: null, minWidth: '25vw' },
        render: getProjects,
      },
      {
        title: '',
        align: 'center',
        render: rowData => {
          const hasEditAccess = canEditMember(rowData);
          const hasRemoveAccess = canManage && rowData._id != appState.getUserId();
          const canRenewInvitation = canManage && !rowData?.isActive && rowData.invitationCodeExpirationDate && isAfter(new Date(), new Date(rowData.invitationCodeExpirationDate));
          const options = [];
          if (hasEditAccess) {
            options.push({
              label: t('table.row.tools.edit', 'Redigera'),
              action: () => onEditMember(rowData),
            });
          }
          if (hasRemoveAccess) {
            options.push({
              label: t('table.row.tools.delete.label', 'Ta bort'),
              action: () => {
                setCurrentMemberToRemoveOrRenew(rowData);
                setConfirmationModalTitle(t('modal.confirmRemove.title', 'Bekräfta borttagning av användare'));
                setShowRemoveUserConfirmationModal(true);
              },
            });
          }
          if (canRenewInvitation) {
            options.push({
              label: t('table.row.tools.renewInvitation.label', 'Förnya inbjudan'),
              action: () => {
                setCurrentMemberToRemoveOrRenew(rowData);
                setConfirmationModalTitle(t('modal.confirmRenewInvitation.title', 'Bekräfta förnyelse av inbjudan'));
                setShowRenewInvitationConfirmationModal(true);
              },
            });
          }
          return (
            <div
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <OptionsMenu
                style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}
                options={options}
              />
            </div>
          );
        },
        cellStyle: {
          width: '30px',
        },
      },
    ];
  };

  const getProjects = rowData => {
    if (rowData?.projects?.length) {
      const projectNames = Array.from(
        rowData?.projects
          ?.map(p => ` ${p?.projectId?.name}`)
          .reduce((set, name) => set.add(name), new Set())
        ).toSorted((a,b) => a.localeCompare(b, undefined, {numeric: true}));

      return <OverflowTip content={projectNames} />;
    }
    return '';
  };

  const handleSearchUsers = searchedValue => {
    setSearchValue(searchedValue);
  };

  const onEditMember = currentMember => {
    setScreenLoader(true);
    setCurrentMemberToEdit(currentMember);
    setAddMemberModalOpen(true);
  };

  const updateMemberLocally = (updatedMember) => {
    let _allColleagues = [...allColleagues]
    const updatedIdx = _allColleagues.findIndex(c => c._id === updatedMember?._id);

    _allColleagues[updatedIdx] = updatedMember;

    setColleaguesLocally(_allColleagues);
    setCurrentMemberToEdit(null);
  }

  const addMemberLocally = (newMember) => {
    let _allColleagues = [...allColleagues]
    const updatedIdx = _allColleagues.findIndex(c => c._id === newMember?._id);

    if(updatedIdx > -1) _allColleagues[updatedIdx] = newMember;
    else _allColleagues = [newMember, ...allColleagues];

    setColleaguesLocally(_allColleagues);
    setCurrentMemberToEdit(null);
    if (appState.isSalesActive() && !appState.isTeamComplete() && _allColleagues.length >= 2) {
      appState.setTeamComplete(true);
    }
  }

  const setColleaguesLocally = (_allColleagues) => {
    setAllColleagues(_allColleagues);
  }

  const toggleBtnLoader = (value) => setSubmitBtnLoader(value);

  return (
    <>
      {screenLoader && createPortal(<Loading />, document.body)}
      <div className="internChatMenuContainer">
        <SearchBarWithActions
          value={searchValue}
          onChange={handleSearchUsers}
        >
          <div className="selector-row">
            <BtChecklistSelect
              placeholder={t('table.header.role', 'Roll')}
              options={roleFilterOptions}
              value={roleFilter}
              onChange={setRoleFilter}
              matchesQuery={matchesLabelOrValue}
            />
          </div>
        </SearchBarWithActions>
        <div className="internTableContainer">
          <MaterialTable
            localization={{ body: { emptyDataSourceMessage: t('table.emptyMessage', 'Kunde inte hitta något resultat') } }}
            options={{
              sorting: false,
              draggable: false,
              search: false,
              toolbar: false,
              detailPanelColumnStyle: { display: 'none' },
              rowStyle: () => ({ color: 'var(--gray-800)' }),
            }}
            tableRef={tableRef}
            style={{ margin: '25px 0' }}
            columns={getColumns()}
            data={colleagues}
            components={{ Pagination: () => null }}
            isLoading={loading}
            onRowClick={canEditRows ? (e, rowData) => {
              if (canEditMember(rowData)) onEditMember(rowData);
            } : undefined}
          ></MaterialTable>
        </div>
      </div>
      <Modal
        title={currentMemberToEdit ? t('modal.edit.title', 'Redigera användare') : t('modal.add.title', 'Lägg till användare')}
        center
        name="modal-add-member"
        show={addMemberModalOpen}
        buttonInfo={{
          label: currentMemberToEdit ? t('modal.edit.saveButton', 'Spara') : t('modal.add.saveButton', 'Lägg till'),
          action: () => appAddMemberRef?.current?.handleSubmit(),
          loaderShow: submitBtnLoader,
          disabled: submitBtnLoader
        }}
        setShow={() => {
          if(submitBtnLoader) return;
          setAddMemberModalOpen(false)
          setTimeout(() => {
            if (appAddMemberRef?.current?.reset) appAddMemberRef.current.reset();
            setCurrentMemberToEdit(null);
          }, 300);
        }}
      >
        <AddMember
          projects={projectList}
          currentMember={currentMemberToEdit}
          ref={appAddMemberRef}
          updateMemberLocally={updateMemberLocally}
          addMemberLocally={addMemberLocally}
          onClose={() => {
            setAddMemberModalOpen(false)
            setTimeout(() => {
              if (appAddMemberRef?.current?.reset) appAddMemberRef.current.reset();
              setCurrentMemberToEdit(null);
            }, 300);
          }}
          toggleBtnLoader={toggleBtnLoader}
        />
      </Modal>
      {(showRemoveUserConfirmationModal || showRenewInvitationConfirmationModal) && <Modal
        title={confirmationModalTitle}
        center
        name="confirmation-modal"
        show={showRemoveUserConfirmationModal || showRenewInvitationConfirmationModal}
        setShow={(value) => {
          setShowRemoveUserConfirmationModal(value);
          setShowRenewInvitationConfirmationModal(value);
          if(!value) {
            setCurrentMemberToRemoveOrRenew(null);
          }
        }}
        buttonInfo={{
          label: t('modal.button.confirm', 'Bekräfta'),
          action: () => onRemoveOrRenewConfirmation(),
        }}
        onCancelButtonClick={() => {
          setShowRemoveUserConfirmationModal(false);
          setShowRenewInvitationConfirmationModal(false);
          setCurrentMemberToRemoveOrRenew(null);
        }}
      >
        {showRemoveUserConfirmationModal && <p>{t('modal.confirmRemove.description', 'Är du säker på att du vill ta bort användaren med e-postadressen {{email}}?', { email: currentMemberToRemoveOrRenew?.email })}</p>}
      </Modal>}
    </>
  );
};

export default UserList;
